// Constants
import {
  ActionTypes,
  GRAPH_NODE_TYPE,
  GRAPH_NODE_DISPLAY_TEXT,
} from 'constants/MogamboConstants';

// Bot Store
import BotStore from '@src/store';
import GraphStore from '@src/stores/GraphStore';
import CommonStore from '@src/stores/CommonStore';

// Actions
import {
  userHasPermission,
  permissionErrorAlert,
  sendGoogleAnalyticsEvents,
} from './_utils';
import { saveNodePositions } from './BotActions';
import { navToSubStory } from './SubstoryActions';

// Utils
import { dispatch } from '../dispatcher';
import { PERMISSION_LEVELS, GOOGLE_ANALYTICS } from '../const';

// API
import * as Api from '../api';
import TemplateStore from '../stores/TemplateStore';

export async function getAllTemplatesForEditor(callback) {
  if (!userHasPermission(PERMISSION_LEVELS.TEMPLATES_VIEW)) {
    return permissionErrorAlert();
  }

  dispatch(ActionTypes.FETCH_TEMPLATES, {
    message: 'Fetching all templates',
  });

  const response = await Api.fetchTemplates();
  if (response.success) {
    const { body, meta } = response;

    dispatch(ActionTypes.FETCH_TEMPLATES_SUCCESS, {
      data: body,
      meta,
      callback,
    });
  } else {
    dispatch(ActionTypes.FETCH_TEMPLATES_FAILED, {
      message:
        response.error ||
        'There was an error in fetching the templates information',
    });
  }
}

export async function getAllTemplatesForSidebar(filters = {}, callback) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_VIEW)) {
    return permissionErrorAlert();
  }

  dispatch(ActionTypes.FETCH_TEMPLATES, {
    message: 'Fetching all templates',
  });

  // Todo: Add filters to fetch only active & published templates for the sidebar
  // as inactive or templates in progress should not be shown to other users.
  const response = await Api.fetchTemplates(filters);
  if (response.success) {
    const { body, meta } = response;

    dispatch(ActionTypes.FETCH_TEMPLATES_SUCCESS, {
      data: body,
      meta,
      callback,
    });
  } else {
    dispatch(ActionTypes.FETCH_TEMPLATES_FAILED, {
      message:
        response.error ||
        'There was an error in fetching the templates information',
    });
  }
}

export function createTemplate(params, ErrorCallback) {
  if (!userHasPermission(PERMISSION_LEVELS.SMART_SKILLS_VIEW_EDIT)) {
    return permissionErrorAlert();
  }

  dispatch(ActionTypes.CREATE_TEMPLATES, {
    message: 'Creating a new template',
  });

  const templateData = {
    name: params.templateName,
    description: params.templatePurpose,
    tags: [params.templateCategory],
    template_type: params.templateType,
  };

  Api.updateTemplate(templateData, (response) => {
    if (response.success) {
      const { body, meta } = response;
      dispatch(ActionTypes.CREATE_TEMPLATES_SUCCESS, {
        data: body,
        meta,
      });
    } else {
      dispatch(ActionTypes.CREATE_TEMPLATES_FAILED, {
        message:
          response.error || 'There was an error in creating the template',
      });
      ErrorCallback(response.error);
    }
  });
}

export function resetCreateTemplateModalStatus() {
  dispatch(ActionTypes.UPDATE_CREATE_TEMPLATE_MODAL_STATUS, {
    created: false,
    botId: null,
  });
}

export function updateTemplateData(templateId, templateData) {
  dispatch(ActionTypes.UPDATE_TEMPLATE_DATA, {
    templateId,
    templateData,
  });
}

export async function saveTemplateData(ErrorCallback) {
  if (!userHasPermission(PERMISSION_LEVELS.SMART_SKILLS_VIEW_EDIT)) {
    return permissionErrorAlert();
  }

  const templateData = TemplateStore.getTemplateData();
  Api.updateTemplate(templateData, (response) => {
    if (response.success) {
      const { body, meta } = response;

      dispatch(ActionTypes.UPDATE_TEMPLATES_SUCCESS, {
        data: body,
        meta,
        message: 'The updates to the template have been saved successfully.',
      });
    } else {
      dispatch(ActionTypes.UPDATE_TEMPLATES_FAILED, {
        message:
          response.error || 'There was an error in creating the template',
      });
      ErrorCallback(response.error);
    }
  });
}

export function fetchTemplateData(templateId, ErrorCallback) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_VIEW)) {
    return permissionErrorAlert();
  }

  dispatch(ActionTypes.FETCH_TEMPLATE_DATA, {
    message: 'Fetching template details',
  });

  Api.fetchTemplates({
    id: templateId,
    full: true,
  })
    .then((response) => {
      if (response.success) {
        const { body, meta } = response;

        dispatch(ActionTypes.FETCH_TEMPLATE_DATA_SUCCESS, {
          id: templateId,
          data: body[0],
          meta,
        });
      } else {
        dispatch(ActionTypes.FETCH_TEMPLATE_DATA_FAILED, {
          message:
            response.error ||
            'There was an error in fetching the templates information',
        });
      }
    })
    .catch((error) => {
      Api.captureException(error);
      ErrorCallback(error);
    });
}

/**
 * This function will reposition the nodes of the recently added smart skills
 * It modifies the x and y coordinates of a node before saving it in the store
 * Newly added smart skill will be added to the bottom of the existing graph
 * It returns the modified values of graph nodes.
 */
function repositionSmartSkill(skillsGraphValues) {
  if (!skillsGraphValues || !skillsGraphValues[0] || !skillsGraphValues[1]) {
    return;
  }

  const values = skillsGraphValues;
  const graph = values[0]?.blocks;
  const skills = values[1];
  const smartSkillId = skills[skills.length - 1]?.id; // Newly added Smart Skill ID
  const nodeList = graph.filter((nodes) => nodes.sub_story_id === smartSkillId);
  const remainingNodeList = graph.filter(
    (nodes) => nodes.sub_story_id !== smartSkillId
  );
  let yMax = 0; // This value is calculated for the existing graph to find out the y max
  let yMin = nodeList?.[0]?.position.y || 0; // It is calculated to make y coordinate of nodes of smart skills zero
  remainingNodeList.forEach((node) => {
    yMax = node.position.y > yMax ? node.position.y : yMax;
  });
  nodeList.forEach((node) => {
    if (node.active) {
      yMin = node.position.y < yMin ? node.position.y : yMin;
    }
  });
  nodeList.forEach(async (node) => {
    if (node.active) {
      // Existing value is subtracted from yMin of the skill
      // yMax is added to reposition it below the existing graph
      // 500 is added to keep a margin between skills on the graph
      node.position.y = node.position.y - yMin + yMax + 500;
      let nodeType;
      // Node type returned in the skills call have different codes for the node type
      // That's why sending different values to saveNodePositions call

      switch (node.block_type) {
        case GRAPH_NODE_TYPE.OUTPUT:
          nodeType = GRAPH_NODE_DISPLAY_TEXT.OUTPUT;
          break;
        case GRAPH_NODE_TYPE.CODE:
          nodeType = GRAPH_NODE_DISPLAY_TEXT.CODE;
          break;
        case GRAPH_NODE_TYPE.STATIC:
          nodeType = GRAPH_NODE_DISPLAY_TEXT.STATIC;
          break;
      }
      // This call will save the coordinates for each node
      await saveNodePositions(
        node.block_id,
        nodeType,
        node.position.x,
        node.position.y
      );
    }
  });
  values[0].blocks = [...remainingNodeList, ...nodeList];
  return values;
}

export async function addTemplateToStory(
  template,
  destinationBot,
  destinationSubStoryName,
  callback
) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT)) {
    return permissionErrorAlert();
  }

  const subStoryData = [];
  template.substory_name.map((substoryName, index) => {
    subStoryData.push({
      from: substoryName,
      to: Array.isArray(destinationSubStoryName)
        ? destinationSubStoryName[index]
        : destinationSubStoryName,
      smart_skill: true,
    });
  });

  Api.copyPasteTemplateAsync(
    {
      copy_component: 'substory',
      copying_options: {
        copy_node_user_says: 1,
        copy_node_bot_says: 1,
        copy_node_connections: 1,
      },
      data: {
        domain: [
          {
            from: template.domain_name,
            to: destinationBot.domain.title,
            story: [
              {
                from: template.story_name,
                to: destinationBot.story.title,
                substory: subStoryData,
              },
            ],
          },
        ],
      },
    },
    (response) => {
      if (response.success) {
        const { body, meta } = response;
        const cas = BotStore.getCAS();
        const event = `cas-${cas}.template-${template.name}.bot-${destinationBot.story.title}`;
        const metaData = CommonStore.getMetaTags();

        sendGoogleAnalyticsEvents(
          GOOGLE_ANALYTICS.CATEGORY.TEMPLATE,
          GOOGLE_ANALYTICS.EVENTS.USE_TEMPLATE,
          event,
          GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
        );

        dispatch(ActionTypes.ADD_TEMPLATE_TO_BOT_SUCCESS, {
          data: body,
          meta,
          message: `The ${
            template.isSmartSkill ? 'Smart Skill' : 'template'
          } has been added to your bot.`,
        });

        Api.getSubstoryGraph(
          destinationBot.story.id,
          () => {
            const skills = GraphStore.getGraph();
            const newSkillId = skills[skills.length - 1].id;
            navToSubStory(metaData, newSkillId);

            // TODO : Zoom the Graph to newly added smart skill.
          },
          repositionSmartSkill
        );
        callback && callback();
      } else {
        dispatch(ActionTypes.ADD_TEMPLATE_TO_BOT_FAILED, {
          message:
            response.error ||
            `There was an error in adding this ${
              template.isSmartSkill ? 'Smart Skill' : 'template'
            } to your bot`,
        });
      }
    },
    destinationBot.domain.id
  );
}

export async function addGeneralNodeTemplateToBot(template, destinationBot) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT)) {
    return permissionErrorAlert();
  }

  Api.copyPasteTemplateAsync(
    {
      copy_component: 'domain_general_nodes',
      copying_options: {},
      data: {
        domain: [
          {
            from: template.name,
            to: destinationBot.domain.title,
          },
        ],
      },
    },
    (response) => {
      if (response.success) {
        const { body, meta } = response;

        dispatch(ActionTypes.ADD_TEMPLATE_TO_BOT_SUCCESS, {
          data: body,
          meta,
          message: 'The template has been added to your bot.',
        });

        Api.getSubstoryGraph(destinationBot.story.id);
      } else {
        dispatch(ActionTypes.ADD_TEMPLATE_TO_BOT_FAILED, {
          message:
            response.error ||
            'There was an error in adding this template to your bot',
        });
      }
    },
    destinationBot.domain.id
  );
}
